import { useMemo } from "react"
import _ from "lodash"
import { useGeoLocation } from "providers/geolocation"
import { useGetServices } from "providers/venue/modules/data/services/useGetServices"
import { IServiceConfig } from "providers/venue/types"
import { DEFAULT_VENUE_FILTER } from "constant"
import { useVenue } from "providers/venue"
import { getFeatureIDByPath } from "utils/feature"
import config from "config"
import { canShowOnDevice } from "hooks/service"

interface IServiceResult {
  hasData: boolean
  services: IServiceConfig[]
  evenColServices: IServiceConfig[]
  oddColServices: IServiceConfig[]
  locationVenueId: string
}

type UseService = () => IServiceResult

const device = config("device")

export const useService: UseService = () => {
  const { findFeatureById, defaultVenue } = useVenue()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")

  const { data: services = [] } = useGetServices()

  const servicesFilteredByVenue = useMemo(() => {
    return _.compact(
      services.map((service) => {
        if (!canShowOnDevice(service.target_device, device)) return null

        const isAllVenue = defaultVenue?.id === DEFAULT_VENUE_FILTER
        const serviceVenueId = service.venue_id
        // Return null if the service is tied to a specific venue that doesn't match the current venue filter — to be filtered out later.
        if (
          !isAllVenue &&
          serviceVenueId &&
          serviceVenueId !== defaultVenue?.id
        )
          return null

        let filteredLocation = service.locations

        if (!isAllVenue) {
          filteredLocation = filteredLocation.filter((location) => {
            const featureId = getFeatureIDByPath(location.url)
            const feature = findFeatureById(featureId)

            // Keep locations that do not use a feature ID.
            if (!feature) return true

            // Filter locations by the specified venue filter ID.
            return feature?.properties?.venue_id === defaultVenue?.id
          })
        }

        return {
          ...service,
          locations: filteredLocation,
        }
      })
    )
  }, [defaultVenue?.id, findFeatureById, services])

  const [evenColServices, oddColServices] = useMemo(() => {
    return servicesFilteredByVenue.reduce(
      (accumulator, currentValue, index) => {
        accumulator[index % 2].push(currentValue)
        return accumulator
      },
      [[], []]
    )
  }, [servicesFilteredByVenue])

  const hasData = _.size(servicesFilteredByVenue) > 0

  return {
    services: servicesFilteredByVenue,
    evenColServices,
    oddColServices,
    hasData,
    locationVenueId,
  }
}
